import { useState } from 'react';
import { useTranslation } from '../../lib/i18n';
import { maestroActionSentence, workingSentence, type MaestroActionRow, type QuickViewRow, type QuickViewTask } from '../../lib/island-quickview';
import { avatarInitials, avatarSlotFor } from '../../lib/avatar-color';
import { presentSharedFile } from '../../lib/outreach-rows';
import { timeAgo } from '../../lib/notification-time';
import { AgentDot } from '../AgentDot';
import { MaestroMark } from './MaestroMark';
import { Avatar, Badge, TextButton } from './ui';
import { TaskDecisionPrompts } from './tasks/TaskDecisionPrompts';
import styles from './AgentsIslandQuickViewRow.module.css';

/** BI for this card — no `page_version` in use on this surface (every `logPageClickBi` call here
 *  already omits it), so the decision block gets the same bare `page_name`. */
const ISLAND_PAGE = { page_name: 'agents-island-quickview' };

// ONE row of the agents-island quick-view. Its own file because the panel around it drags in the task
// modals (and, through them, the markdown renderer), and the row's affordance — the part a reader has to
// be able to tell apart at a glance — is worth being able to render on its own in a test.

const INK = 'var(--sym-ink)';
const INK_2 = 'var(--ds-text-secondary)';

const META_LINE: React.CSSProperties = {
  fontSize: 12,
  lineHeight: '16px',
  fontWeight: 'var(--sym-weight-semibold)',
  letterSpacing: '-0.24px',
  color: INK_2,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};


/** The row's lead line (title / question / outreach subject): 14/18, primary, one ellipsized line. Weight
 *  is Medium for a task/decision and SemiBold for an outreach subject (Figma). */
const LEAD_LINE: React.CSSProperties = {
  fontSize: 14,
  lineHeight: '18px',
  letterSpacing: '-0.42px',
  color: INK,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

/** A waiting title wraps to TWO lines before it ellipsizes, where every other lead line on this card is
 *  one `nowrap` line. That is the whole point of the list: sibling tasks from one plan are routinely
 *  identical up to their last word ("…invitation to friend 1 / 2 / 3"), so a single ellipsized line
 *  renders three visually identical rows — which is the duplicate-looking-rows report this card was
 *  built to answer, reproduced inside the answer. Caught by rendering it, not by a test. Shared 1:1 with
 *  iOS `lineLimit(2)` and Android `maxLines = 2`. */
const WAITING_TITLE_LINE: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 'var(--sym-weight-medium)',
  lineHeight: '18px',
  letterSpacing: '-0.42px',
  color: INK,
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
};

/** How many task titles a question card lists before it folds the rest into "+{n} more". Three, like the
 *  card's own row cap — a question blocking a twelve-task plan must not turn one card into the list it
 *  exists to replace. */
const WAITING_TITLES_SHOWN = 3;

/** The trailing origin word on a task row's meta line — "Routine" / "Goal" (Tiny/SemiBold, PRIMARY ink). */
const ORIGIN_LABEL: React.CSSProperties = {
  fontSize: 12,
  lineHeight: '16px',
  fontWeight: 'var(--sym-weight-semibold)',
  letterSpacing: '-0.24px',
  color: INK,
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

/** ONE row of the orange "action needed" card (Figma 17339-142828 / 17666-231716). META-first (like the
 *  blue card), with three shapes — a TASK (from a routine/goal), a DECISION (answer chips), or an OUTREACH
 *  (sender avatar + Reply). No status badge and no origin FOLD anymore: each action-required task is its own
 *  row, carrying a small "Routine"/"Goal" origin word on its meta line. Exported for the behavior tests. */
export function QuickViewRowButton({
  task,
  agentName,
  onOpen,
  onOpenSharedFile,
}: {
  task: QuickViewRow;
  agentName?: string;
  onOpen: (task: QuickViewRow) => void;
  /** Opens a file someone shared, by id. Optional so a surface with nowhere to render a viewer keeps
   *  the thread hand-off — the row then behaves exactly as it did before shares were distinguishable. */
  onOpenSharedFile?: (fileId: string, fileName?: string) => void;
}) {
  const { t } = useTranslation();
  // Only a signal to re-render after a decision lands (the row re-reads from cache); no local flag is read
  // since the status badge is gone.
  const [, setAnsweredHere] = useState(false);

  const rowButton: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 12, // meta line ↔ body (Figma spacing/12)
    padding: 0, // flush to the card's 20px content inset; the 14 row rhythm rides the container gap
    borderRadius: 12,
    textAlign: 'left',
    width: '100%',
    border: 'none',
    background: 'transparent',
    font: 'inherit',
    cursor: 'pointer',
    color: INK,
  };

  // OUTREACH — its subject is a person, not a piece of work: the META shows the SENDER (20px initials
  // avatar + name, no agent dot / workspace / origin word), the LEAD is the outreach's own subject in
  // SemiBold, and "Reply" opens the thread (the same place tapping the row goes).
  if (task.isOutreach) {
    const sender = task.senderName ?? '';
    // A SHARE is not correspondence. The row still belongs to the person who sent it — their name and
    // avatar stay on the meta line — but its subject is a FILE, so it is titled by the file and its
    // action opens it. Reply is what this row offered before anything on the wire said it was a share,
    // and it is the one action that cannot do what the reader came for.
    // The PRESENCE GATE is the shared rule, not `task.sharedFile` itself: a notice with no `fileId`
    // must never make this row title itself after a file or offer a View that opens nothing. The route
    // already refuses to stamp one, and both phones gate in their store parsers — gating on object
    // presence here trusted one producer to keep doing that.
    const shared = presentSharedFile(task.sharedFile);
    // Reply is a SIBLING of the row button, never nested inside it — a button inside a button is invalid
    // HTML and the tap would also bubble into the row's open handler. Matches iOS/Android (Reply beside
    // the row's own tap target). The outer column carries the 8px subject↔Reply gap (Figma).
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <button type="button" onClick={() => onOpen(task)} data-outreach-row style={rowButton}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            <Avatar size={20} initials={avatarInitials(sender)} color={avatarSlotFor(sender)} />
            <span style={META_LINE}>{sender || t('tasks.outreach.untitled')}</span>
          </span>
          <span style={{ ...LEAD_LINE, fontWeight: 'var(--sym-weight-semibold)', letterSpacing: '-0.28px' }}>
            {shared
              ? shared.fileName || t('tasks.outreach.sharedFileUntitled')
              : task.title || t('tasks.outreach.untitled')}
          </span>
        </button>
        <span style={{ display: 'flex' }}>
          <TextButton size="small" underline onClick={() => (shared ? onOpenSharedFile?.(shared.fileId, shared.fileName) : onOpen(task))}>
            {shared ? t('tasks.outreach.viewFile') : t('chat.reply.action')}
          </TextButton>
        </span>
      </div>
    );
  }

  // TASK / DECISION — "{Agent} | {Workspace}" on the meta line + a right-aligned "Routine"/"Goal" origin
  // word when the task has one; the title (Medium) as the lead; a goal task also gets a subtitle; and the
  // answer chips, when a decision is open, below the row (a button can't nest the chip buttons).
  //
  // A QUESTION CARD (`task.question`) is the same anatomy with its body rearranged: one question, the
  // chips that answer it, and then the TITLES of every task waiting on that answer. Its lead title is
  // dropped, because the card stands for N tasks and that list names them all — printing one of them
  // above the question is the "three identical-looking rows" defect in miniature, and printing the
  // representative's title twice (once as the lead, once in the list) is just noise. The card says what
  // is waiting and nothing about what answering will release: the branch a task belongs to
  // (`DecisionBlocker.requiredChoice`) never reaches the client, so "releases 3 tasks" would be unknowable.
  const question = task.question;
  const identity =
    agentName && task.workspaceName ? `${agentName} | ${task.workspaceName}` : agentName || task.workspaceName || '';
  const originLabel = task.pulseIdentityId
    ? t('agentsIsland.quickView.originRoutine')
    : task.goalId
    ? t('agentsIsland.quickView.originGoal')
    : null;
  const subtitle = task.goalId ? task.summary?.trim() : undefined;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <button type="button" onClick={() => onOpen(task)} style={rowButton}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flex: 1 }}>
            {/* The Maestro is deliberately OUT of the agent colour system (see the blue row above and
                FileCreatedBy): an unattributed row (no `agentId`) is the team lead's, so it wears the
                MaestroMark, never a blank slot. */}
            {task.agentId ? <AgentDot id={task.agentId} size={12} /> : <MaestroMark size={12} />}
            {identity ? <span style={META_LINE}>{identity}</span> : null}
          </span>
          {originLabel ? <span style={ORIGIN_LABEL}>{originLabel}</span> : null}
        </span>
        {/* A question card drops the lead title — its own body names every task waiting on the
            answer, and one of those titles printed above the question is where the duplicate-looking
            rows came from. */}
        {question ? null : (
          <span style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
            {/* Figma 17339-142828: a GOAL title reads SemiBold 14/18 (like the outreach subject). A
                ROUTINE title (a routine/pulse row) also reads SemiBold 14/18; a plain decision title
                stays Medium 14/18. */}
            <span
              style={{
                ...LEAD_LINE,
                ...(task.goalId && !task.pulseIdentityId
                  ? { fontWeight: 'var(--sym-weight-semibold)', letterSpacing: '-0.28px' }
                  : task.pulseIdentityId
                  ? { fontWeight: 'var(--sym-weight-semibold)' }
                  : { fontWeight: 'var(--sym-weight-medium)' }),
              }}
            >
              {task.title || t('tasks.untitledTask')}
            </span>
            {subtitle ? (
              <span style={{ fontSize: 14, lineHeight: '20px', letterSpacing: '-0.28px', color: INK, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {subtitle}
              </span>
            ) : null}
          </span>
        )}
      </button>
      {/* The question this row is held on, answered in place. Rendered unconditionally — it draws the answer
          chips for a blocked task, the "unavailable" line when the prompt could not be read, and NOTHING
          otherwise (so most action-required rows show no extra). Outside the button — a button can't nest
          the answer buttons. `origin={null}`: no goal title travels with the row, so no unresolvable link.

          A question card answers `question.prompt` ALONE and not everything its representative task holds:
          a task blocked on two questions sits under BOTH cards, and without the override each of those two
          cards would draw both questions. `taskCount` rides the card's own BI (`task_count`) — the events
          keep the representative task id, so the fan-out is readable from the count rather than inferred. */}
      <TaskDecisionPrompts
        task={task}
        prompts={question ? [question.prompt] : undefined}
        taskCount={question ? question.tasks.length : undefined}
        origin={null}
        onAnsweredChange={setAnsweredHere}
        page={ISLAND_PAGE}
        pillChips
      />
      {question ? <WaitingOnThisAnswer tasks={question.tasks} /> : null}
    </div>
  );
}

/**
 * The tasks held on ONE answer, under "Waiting on this answer" — the body of a question card.
 *
 * Titles only, and deliberately not links or buttons: the card's affordance is answering the question, and
 * N tappable titles would be N ways to leave the one screen built to let the reader stay. Capped at
 * {@link WAITING_TITLES_SHOWN} with a "+{n} more" line, so a plan with twelve blocked tasks is still a card.
 */
function WaitingOnThisAnswer({ tasks }: { tasks: QuickViewTask[] }) {
  const { t } = useTranslation();
  const shown = tasks.slice(0, WAITING_TITLES_SHOWN);
  const more = Math.max(0, tasks.length - shown.length);
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }} data-waiting-on-answer={tasks.length}>
      <span style={META_LINE}>{t('agentsIsland.quickView.waitingOnThisAnswer')}</span>
      {shown.map((waiting) => (
        <span key={waiting.id} style={{ ...WAITING_TITLE_LINE }} dir="auto">
          {waiting.title || t('tasks.untitledTask')}
        </span>
      ))}
      {more > 0 ? <span style={META_LINE}>{t('agentsIsland.quickView.moreTasksOnAnswer', { count: more })}</span> : null}
    </span>
  );
}

/** The action sentence line — the row's LEAD (Figma "Frame 2147242241"): Wix Madefor Medium 14/18,
 *  Text&Icon/Primary. Wraps to TWO lines, then ellipsizes (per product — the frame draws one line, but a
 *  longer action reads better across two than truncated at one). Always primary ink, including the
 *  "Working on: {title}" fallback — the frame draws no muted case. */
const SENTENCE_LINE: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 'var(--sym-weight-medium)',
  lineHeight: '18px',
  letterSpacing: '-0.42px',
  color: INK,
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
};

/**
 * ONE row of the blue "agents at work" card (Figma 17339-98544). The META line leads and the action
 * leads visually — the inverse of the pre-redesign agent-first row:
 *
 *   - top META line: a small avatar wearing the synchronized HEARTBEAT (the pill's single-agent beat,
 *     scale 1→1.18→1 over 1.7s; every row in the card beats in unison — see `.beat`), then
 *     "{Agent} | {Workspace}" in the Tiny/SemiBold secondary ink, a REVIEWING badge when IN_REVIEW, and
 *     the compact elapsed ("2m") pushed to the trailing edge. A task with no `agentId` is the Maestro's,
 *     shown under the user's own (renameable) assistant name; a task with no workspace drops the "| …".
 *   - the ACTION sentence below, by the shared chain (`workingSentence`): `currentAction` → the first
 *     unticked plan step → the localized "Working on: {title}". No separate title line (the frame has none).
 *
 * Tap opens the task, exactly as a peek row does. Exported for the behavior tests.
 */
export function WorkingRowButton({
  task,
  agentLabel,
  onOpen,
}: {
  task: QuickViewTask;
  /** The resolved display name: the roster's name for `task.agentId`, or the Maestro's own. */
  agentLabel: string;
  onOpen: (task: QuickViewTask) => void;
}) {
  const { t } = useTranslation();
  const sentence = workingSentence(task);
  const title = task.title || t('tasks.untitledTask');
  const elapsed = timeAgo(task.lastActivityAt ?? null);
  const heroText = sentence.kind === 'title' ? t('agentsIsland.quickView.workingOn', { title }) : sentence.text;
  const identity = task.workspaceName ? `${agentLabel} | ${task.workspaceName}` : agentLabel;
  return (
    <button
      type="button"
      onClick={() => onOpen(task)}
      data-working-row
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 12,
        padding: 0, // rows flush to the card's 20px content inset; the 14px row rhythm rides the container gap (Figma)
        borderRadius: 12,
        textAlign: 'left',
        width: '100%',
        border: 'none',
        background: 'transparent',
        font: 'inherit',
        cursor: 'pointer',
        color: INK,
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flex: 1 }}>
          <span className={styles.beat} aria-hidden>
            {/* The Maestro is deliberately OUT of the agent colour system (see FileCreatedBy) — an
                unattributed live run is the team lead's, never a hued dot pretending to be a teammate. */}
            {task.agentId ? <AgentDot id={task.agentId} size={14} /> : <MaestroMark size={14} />}
          </span>
          <span style={META_LINE}>{identity}</span>
          {task.status === 'IN_REVIEW' ? (
            <Badge variant="in-progress" label={t('agentsIsland.quickView.reviewing')} style={{ flexShrink: 0 }} />
          ) : null}
        </span>
        {elapsed ? <span style={{ ...META_LINE, flexShrink: 0 }}>{elapsed}</span> : null}
      </span>
      <span style={SENTENCE_LINE}>{heroText}</span>
    </button>
  );
}

/**
 * ONE Maestro-ACTION row of the blue card — the Maestro personally at work on something that is not a
 * task (a Wixel render, a free-run agent loop). Same anatomy as a working row: the Maestro's mark in the
 * synchronized heartbeat, the user's own (renameable) assistant name on the meta line, compact elapsed
 * from `startedAt` — but no "| {workspace}" (a maestro action has no workspace), no reviewing badge, and
 * the action `label` (else a localized per-kind fallback) as the lead sentence. An empty label falls
 * back rather than rendering an empty line.
 *
 * Tap opens the MAIN CHAT (the surface's existing open-chat navigation): the action lives on the maestro
 * conversation, so the chat is where its progress and outcome will land. Exported for the behavior tests.
 */
export function MaestroActionRowButton({
  action,
  agentLabel,
  onOpen,
}: {
  action: MaestroActionRow;
  /** The Maestro's resolved display name — the same source the rest of the chrome uses. */
  agentLabel: string;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const sentence = maestroActionSentence(action);
  const elapsed = timeAgo(action.startedAt ?? null);
  return (
    <button
      type="button"
      onClick={onOpen}
      data-maestro-action-row={action.kind}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 12,
        padding: 0, // rows flush to the card's 20px content inset; the 14px row rhythm rides the container gap (Figma)
        borderRadius: 12,
        textAlign: 'left',
        width: '100%',
        border: 'none',
        background: 'transparent',
        font: 'inherit',
        cursor: 'pointer',
        color: INK,
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flex: 1 }}>
          <span className={styles.beat} aria-hidden>
            <MaestroMark size={14} />
          </span>
          <span style={META_LINE}>{agentLabel}</span>
        </span>
        {elapsed ? <span style={{ ...META_LINE, flexShrink: 0 }}>{elapsed}</span> : null}
      </span>
      <span style={SENTENCE_LINE}>{sentence.kind === 'label' ? sentence.text : t(sentence.key)}</span>
    </button>
  );
}
